import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { config } from '../../config';
import { fetchWithCSRF } from '../../utils/apiUtils';

export const fetchBoardMetrics = createAsyncThunk(
  'analytics/fetchBoardMetrics',
  async ({ boardId, dateRange = 30 }, { rejectWithValue }) => {
    try {

      const response = await fetchWithCSRF(`${config.API_URI}/v1/analytics/board/${boardId}/metrics?dateRange=${dateRange}`, {
        credentials: 'include',
        headers: {

        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to fetch board metrics';

        return rejectWithValue({
          status: response.status,
          message: errorMessage,
          type: response.status === 403 ? 'ACCESS_DENIED' :
                response.status === 404 ? 'NOT_FOUND' : 'ERROR'
        });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({
        status: 500,
        message: error.message || 'Failed to fetch board metrics',
        type: 'ERROR'
      });
    }
  }
);

export const fetchBoardTimeAnalytics = createAsyncThunk(
  'analytics/fetchBoardTimeAnalytics',
  async ({ boardId, dateRange = 30 }, { rejectWithValue }) => {
    try {

      const response = await fetchWithCSRF(`${config.API_URI}/v1/analytics/board/${boardId}/time-analytics?dateRange=${dateRange}`, {
        credentials: 'include',
        headers: {

        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to fetch time analytics';

        return rejectWithValue({
          status: response.status,
          message: errorMessage,
          type: response.status === 403 ? 'ACCESS_DENIED' :
                response.status === 404 ? 'NOT_FOUND' : 'ERROR'
        });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({
        status: 500,
        message: error.message || 'Failed to fetch time analytics',
        type: 'ERROR'
      });
    }
  }
);

export const fetchBoardFullTimeline = createAsyncThunk(
  'analytics/fetchBoardFullTimeline',
  async ({ boardId, dateRange = 90 }, { rejectWithValue }) => {
    try {

      const response = await fetchWithCSRF(`${config.API_URI}/v1/analytics/board/${boardId}/full-timeline?dateRange=${dateRange}`, {
        credentials: 'include',
        headers: {

        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to fetch full timeline';

        return rejectWithValue({
          status: response.status,
          message: errorMessage,
          type: response.status === 403 ? 'ACCESS_DENIED' :
                response.status === 404 ? 'NOT_FOUND' : 'ERROR'
        });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({
        status: 500,
        message: error.message || 'Failed to fetch full timeline',
        type: 'ERROR'
      });
    }
  }
);

export const fetchBoardUserProductivity = createAsyncThunk(
  'analytics/fetchBoardUserProductivity',
  async ({ boardId, dateRange = 30 }, { rejectWithValue }) => {
    try {

      const response = await fetchWithCSRF(`${config.API_URI}/v1/analytics/board/${boardId}/user-productivity?dateRange=${dateRange}`, {
        credentials: 'include',
        headers: {

        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to fetch user productivity';

        return rejectWithValue({
          status: response.status,
          message: errorMessage,
          type: response.status === 403 ? 'ACCESS_DENIED' :
                response.status === 404 ? 'NOT_FOUND' : 'ERROR'
        });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({
        status: 500,
        message: error.message || 'Failed to fetch user productivity',
        type: 'ERROR'
      });
    }
  }
);

export const fetchBoardWorkflowAnalytics = createAsyncThunk(
  'analytics/fetchBoardWorkflowAnalytics',
  async ({ boardId, dateRange = 30 }, { rejectWithValue }) => {
    try {

      const response = await fetchWithCSRF(`${config.API_URI}/v1/analytics/board/${boardId}/workflow-analytics?dateRange=${dateRange}`, {
        credentials: 'include',
        headers: {

        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to fetch workflow analytics';

        return rejectWithValue({
          status: response.status,
          message: errorMessage,
          type: response.status === 403 ? 'ACCESS_DENIED' :
                response.status === 404 ? 'NOT_FOUND' : 'ERROR'
        });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({
        status: 500,
        message: error.message || 'Failed to fetch workflow analytics',
        type: 'ERROR'
      });
    }
  }
);

export const fetchBoardAnalytics = createAsyncThunk(
  'analytics/fetchBoardAnalytics',
  async ({ boardId, dateRange = 30 }, { rejectWithValue }) => {
    try {

      const response = await fetchWithCSRF(`${config.API_URI}/v1/analytics/board/${boardId}?dateRange=${dateRange}`, {
        credentials: 'include',
        headers: {

        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to fetch board analytics';

        return rejectWithValue({
          status: response.status,
          message: errorMessage,
          type: response.status === 403 ? 'ACCESS_DENIED' :
                response.status === 404 ? 'NOT_FOUND' : 'ERROR'
        });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({
        status: 500,
        message: error.message || 'Failed to fetch board analytics',
        type: 'ERROR'
      });
    }
  }
);

const initialState = {
  metrics: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null
  },
  timeAnalytics: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null
  },
  fullTimeline: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null
  },
  userProductivity: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null
  },
  workflowAnalytics: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null
  },
  boardAnalytics: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null
  },
  currentBoardId: null,
  currentDateRange: 30,
  isInitialized: false
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setAnalyticsParams: (state, action) => {
      const { boardId, dateRange } = action.payload;
      state.currentBoardId = boardId;
      state.currentDateRange = dateRange || 30;
    },
    clearAnalyticsData: (state) => {
      state.metrics.data = null;
      state.timeAnalytics.data = null;
      state.fullTimeline.data = null;
      state.userProductivity.data = null;
      state.workflowAnalytics.data = null;
      state.boardAnalytics.data = null;
      state.isInitialized = false;
    },
    clearComponentData: (state, action) => {
      const component = action.payload;
      if (state[component]) {
        state[component].data = null;
        state[component].error = null;
        state[component].lastFetched = null;
      }
    },
    setAnalyticsInitialized: (state) => {
      state.isInitialized = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBoardMetrics.pending, (state) => {
        state.metrics.loading = true;
        state.metrics.error = null;
      })
      .addCase(fetchBoardMetrics.fulfilled, (state, action) => {
        state.metrics.loading = false;
        state.metrics.data = action.payload.data;
        state.metrics.lastFetched = new Date().toISOString();
      })
      .addCase(fetchBoardMetrics.rejected, (state, action) => {
        state.metrics.loading = false;
        state.metrics.error = action.payload;
      })
      .addCase(fetchBoardTimeAnalytics.pending, (state) => {
        state.timeAnalytics.loading = true;
        state.timeAnalytics.error = null;
      })
      .addCase(fetchBoardTimeAnalytics.fulfilled, (state, action) => {
        state.timeAnalytics.loading = false;
        state.timeAnalytics.data = action.payload.data;
        state.timeAnalytics.lastFetched = new Date().toISOString();
      })
      .addCase(fetchBoardTimeAnalytics.rejected, (state, action) => {
        state.timeAnalytics.loading = false;
        state.timeAnalytics.error = action.payload;
      })
      .addCase(fetchBoardFullTimeline.pending, (state) => {
        state.fullTimeline.loading = true;
        state.fullTimeline.error = null;
      })
      .addCase(fetchBoardFullTimeline.fulfilled, (state, action) => {
        state.fullTimeline.loading = false;
        state.fullTimeline.data = action.payload.data;
        state.fullTimeline.lastFetched = new Date().toISOString();
      })
      .addCase(fetchBoardFullTimeline.rejected, (state, action) => {
        state.fullTimeline.loading = false;
        state.fullTimeline.error = action.payload;
      })
      .addCase(fetchBoardUserProductivity.pending, (state) => {
        state.userProductivity.loading = true;
        state.userProductivity.error = null;
      })
      .addCase(fetchBoardUserProductivity.fulfilled, (state, action) => {
        state.userProductivity.loading = false;
        state.userProductivity.data = action.payload.data;
        state.userProductivity.lastFetched = new Date().toISOString();
      })
      .addCase(fetchBoardUserProductivity.rejected, (state, action) => {
        state.userProductivity.loading = false;
        state.userProductivity.error = action.payload;
      })
      .addCase(fetchBoardWorkflowAnalytics.pending, (state) => {
        state.workflowAnalytics.loading = true;
        state.workflowAnalytics.error = null;
      })
      .addCase(fetchBoardWorkflowAnalytics.fulfilled, (state, action) => {
        state.workflowAnalytics.loading = false;
        state.workflowAnalytics.data = action.payload.data;
        state.workflowAnalytics.lastFetched = new Date().toISOString();
      })
      .addCase(fetchBoardWorkflowAnalytics.rejected, (state, action) => {
        state.workflowAnalytics.loading = false;
        state.workflowAnalytics.error = action.payload;
      })
      .addCase(fetchBoardAnalytics.pending, (state) => {
        state.boardAnalytics.loading = true;
        state.boardAnalytics.error = null;
      })
      .addCase(fetchBoardAnalytics.fulfilled, (state, action) => {
        state.boardAnalytics.loading = false;
        state.boardAnalytics.data = action.payload.analytics;
        state.boardAnalytics.lastFetched = new Date().toISOString();
      })
      .addCase(fetchBoardAnalytics.rejected, (state, action) => {
        state.boardAnalytics.loading = false;
        state.boardAnalytics.error = action.payload;
      });
  }
});

export const {
  setAnalyticsParams,
  clearAnalyticsData,
  clearComponentData,
  setAnalyticsInitialized
} = analyticsSlice.actions;

export const selectMetrics = (state) => state.analytics.metrics;
export const selectTimeAnalytics = (state) => state.analytics.timeAnalytics;
export const selectFullTimeline = (state) => state.analytics.fullTimeline;
export const selectUserProductivity = (state) => state.analytics.userProductivity;
export const selectWorkflowAnalytics = (state) => state.analytics.workflowAnalytics;
export const selectBoardAnalytics = (state) => state.analytics.boardAnalytics;
export const selectAnalyticsParams = (state) => ({
  boardId: state.analytics.currentBoardId,
  dateRange: state.analytics.currentDateRange
});
export const selectIsAnalyticsInitialized = (state) => state.analytics.isInitialized;

const selectMetricsLoading = (state) => state.analytics.metrics.loading;
const selectTimeAnalyticsLoading = (state) => state.analytics.timeAnalytics.loading;
const selectFullTimelineLoading = (state) => state.analytics.fullTimeline.loading;
const selectUserProductivityLoading = (state) => state.analytics.userProductivity.loading;
const selectWorkflowAnalyticsLoading = (state) => state.analytics.workflowAnalytics.loading;

export const selectAnalyticsLoadingState = createSelector(
  [
    selectMetricsLoading,
    selectTimeAnalyticsLoading,
    selectFullTimelineLoading,
    selectUserProductivityLoading,
    selectWorkflowAnalyticsLoading,
  ],
  (
    metricsLoading,
    timeAnalyticsLoading,
    fullTimelineLoading,
    userProductivityLoading,
    workflowAnalyticsLoading
  ) => ({
    metrics: metricsLoading,
    timeAnalytics: timeAnalyticsLoading,
    fullTimeline: fullTimelineLoading,
    userProductivity: userProductivityLoading,
    workflowAnalytics: workflowAnalyticsLoading,
    isAnyLoading:
      metricsLoading ||
      timeAnalyticsLoading ||
      fullTimelineLoading ||
      userProductivityLoading ||
      workflowAnalyticsLoading,
  })
);

export default analyticsSlice.reducer;
